import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { format, parseISO } from "date-fns";
import { useInviteBatchDetail } from "@/hooks/sms/useInviteBatchDetail";
import { skipReasonLabel } from "@/utils/inviteForecast";
import { formatPhoneNumber } from "@/utils/phoneFormatter";
import type { CampaignRecipient, MarketingCampaign } from "@/types/sms";

/** One recipient's status in owner words — what happened to this customer. */
function recipientStatusLabel(r: CampaignRecipient): { text: string; tone: "success" | "muted" | "danger" | "default" } {
  if (r.status === "skipped") return { text: skipReasonLabel(r.skip_reason), tone: "muted" };
  if (r.status === "holdout") return { text: "Held back (comparison group)", tone: "muted" };
  if (r.status === "failed") return { text: "Couldn’t be delivered", tone: "danger" };
  if (r.outcome === "subscribed") return { text: "Joined the text club", tone: "success" };
  if (r.outcome === "opted_out") return { text: "Opted out", tone: "muted" };
  if (r.status === "delivered" || r.status === "sent") return { text: "Invited, no reply yet", tone: "default" };
  if (r.status === "queued" || r.status === "pending") return { text: "Queued to send", tone: "default" };
  return { text: r.status, tone: "default" };
}

const TONE_CLASS: Record<string, string> = {
  success: "text-status-success",
  danger: "text-status-danger",
  muted: "text-muted-foreground",
  default: "text-foreground",
};

interface DetailProps {
  batch: MarketingCampaign | null;
  onOpenChange: (open: boolean) => void;
}

/** The batch-detail dialog: every customer in a day's batch, texted or skipped,
 *  with the skip reason in owner words — "why didn't she get the text?". */
export function InviteBatchDetailDialog({ batch, onOpenChange }: DetailProps) {
  const { data: recipients = [], isLoading } = useInviteBatchDetail(batch?.id ?? null);

  const title = batch
    ? (() => {
        const raw = batch.invite_occurrence_date ?? batch.created_at;
        try {
          return format(parseISO(raw), "EEEE, MMM d");
        } catch {
          return raw.slice(0, 10);
        }
      })()
    : "";

  return (
    <Dialog open={!!batch} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Batch on {title}</DialogTitle>
          <DialogDescription>
            {batch
              ? `${batch.sent_count} invited · ${batch.recipients_count} customers reviewed`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <p className="py-4 text-sm text-muted-foreground">Loading…</p>
          ) : recipients.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">No customers recorded for this batch.</p>
          ) : (
            <ul className="divide-y divide-border">
              {recipients.map((r) => {
                const status = recipientStatusLabel(r);
                return (
                  <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{r.display_name || "No name on file"}</p>
                      <p className="text-xs tabular-nums text-muted-foreground">{formatPhoneNumber(r.customer_phone)}</p>
                    </div>
                    <span className={`text-sm ${TONE_CLASS[status.tone]}`}>{status.text}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
